import React from 'react';
import {
  SliderProps as MuiSliderProps,
  Typography,
  Box,
} from '@mui/material';
import { StyledSlider, SliderContainer } from './Slider.style';

export interface SliderProps extends Omit<MuiSliderProps, 'onChange'> {
  /**
   * The current value of the slider
   */
  value?: number | number[];
  /**
   * Callback fired when the value changes
   */
  onChange?: (value: number | number[]) => void;
  /**
   * The minimum allowed value
   */
  min?: number;
  /**
   * The maximum allowed value
   */
  max?: number;
  /**
   * The step increment
   */
  step?: number;
  /**
   * Whether to show value labels
   */
  valueLabelDisplay?: 'auto' | 'on' | 'off';
  /**
   * Label for the slider
   */
  label?: string;
  /**
   * Whether to show min/max labels
   */
  showMinMax?: boolean;
  /**
   * Custom marks on the slider
   */
  marks?: boolean | Array<{ value: number; label?: string }>;
  /**
   * Whether the slider is disabled
   */
  disabled?: boolean;
  /**
   * The color of the slider
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  /**
   * The size of the slider
   */
  size?: 'small' | 'medium';
}

export const Slider: React.FC<SliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  valueLabelDisplay = 'auto',
  label,
  showMinMax = false,
  marks = false,
  disabled = false,
  color = 'primary',
  size = 'medium',
  ...props
}) => {
  const handleChange = (event: Event, newValue: number | number[]) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <SliderContainer>
      {label && (
        <Typography variant="body2" gutterBottom>
          {label}
        </Typography>
      )}
      
      {showMinMax && (
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="caption" color="textSecondary">
            {min}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {max}
          </Typography>
        </Box>
      )}
      
      <StyledSlider
        value={value}
        onChange={handleChange}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay={valueLabelDisplay}
        marks={marks}
        disabled={disabled}
        color={color}
        size={size}
        {...props}
      />
    </SliderContainer>
  );
}; 